import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Briefcase, Users, Code, Palette, Send } from 'lucide-react';
import { useSubmitTeamApplication } from '../hooks/useQueries';
import { toast } from 'sonner';
import { getCaptchaToken } from '@/service/capcha.service';
import { CaptchaWidget } from '@/components/CapchaWidget';
import { FormUploadService } from '@/service/form.service';

export default function JoinTeamPage() {
  const [ scrollY, setScrollY ] = useState(0);
  const [ formData, setFormData ] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
  });

  // ✅ ADDED
  const [ isSubmitting, setIsSubmitting ] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const submitTeamApplication = useSubmitTeamApplication();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.phone || !formData.position || !formData.experience) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      setIsSubmitting(true);
      let formIns = new FormUploadService()
      await formIns.uploadJobForm(formData)


      toast.success('Application submitted successfully! We\'ll review it and get back to you soon.');
      setFormData({ name: '', email: '', phone: '', position: '', experience: '' });
    } catch (error) {
      console.log("ERROR :", error)
      toast.error('Failed to submit application. Please try again.');
    }
    finally {
      // ✅ ADDED (guaranteed unlock)
      setIsSubmitting(false);
    }
  };

  const positions = [
    {
      icon: Code,
      title: 'Software Engineer',
      description: 'Build and maintain our platform infrastructure',
    },
    {
      icon: Palette,
      title: 'UI/UX Designer',
      description: 'Create beautiful and intuitive user experiences',
    },
    {
      icon: Users,
      title: 'Community Manager',
      description: 'Engage with our user community and gather feedback',
    },
    {
      icon: Briefcase,
      title: 'Product Manager',
      description: 'Drive product strategy and roadmap',
    },
  ];

  return (
    <div className="flex flex-col ">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-yellow via-brand-yellow to-[#FFE566] md:py-16 py-12 bg-brand-yellow py-12">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            transform: `translateY(${scrollY * 0.5}px)`,
            backgroundImage: 'radial-gradient(circle, #004CFF 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }}
        />
        <div className="container relative z-10">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="mb-4 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-5xl font-extrabold tracking-tight text-brand-black md:text-6xl">
              Join Our Team
            </h1>
            <p className="text-xl font-medium text-brand-black md:text-2xl">
              Help us transform education and make a real impact on students' lives around the world
            </p>
          </div>
        </div>
      </section>

      {/* Why Join Section */}
      <section
        className="relative bg-brand-white py-12"
        style={{
          transform: `translateY(${scrollY * 0.1}px)`,
        }}
      >
        <div className="container">
          <div className="mx-auto max-w-4xl">
            <h2 className="mb-6 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-4xl font-extrabold tracking-tight text-brand-black">Why Join UNILinkd?</h2>
            <div className="grid gap-6 md:grid-cols-3">
              <Card className="rounded-3xl bg-brand-white shadow-xl transition-all hover:scale-105 hover:shadow-2xl">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-brand-black">Make an Impact</CardTitle>
                  <CardDescription className="text-base font-medium text-brand-black">
                    Work on products that directly improve education for thousands of students
                  </CardDescription>
                </CardHeader>
              </Card>
              <Card className="rounded-3xl bg-brand-white shadow-xl transition-all hover:scale-105 hover:shadow-2xl">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-brand-black">Grow Your Skills</CardTitle>
                  <CardDescription className="text-base font-medium text-brand-black">
                    Learn from talented colleagues and work with cutting-edge technologies
                  </CardDescription>
                </CardHeader>
              </Card>
              <Card className="rounded-3xl bg-brand-white shadow-xl transition-all hover:scale-105 hover:shadow-2xl">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-brand-black">Flexible Culture</CardTitle>
                  <CardDescription className="text-base font-medium text-brand-black">
                    Remote-friendly environment with flexible hours and work-life balance
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-yellow via-brand-yellow to-[#FFE566] md:py-16 py-12 bg-brand-yellow py-12">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            transform: `translateY(${scrollY * 0.3}px)`,
            backgroundImage: 'linear-gradient(45deg, #004CFF 25%, transparent 25%, transparent 75%, #004CFF 75%, #004CFF), linear-gradient(45deg, #004CFF 25%, transparent 25%, transparent 75%, #004CFF 75%, #004CFF)',
            backgroundSize: '60px 60px',
            backgroundPosition: '0 0, 30px 30px',
          }}
        />
        <div className="container relative z-10">
          <div className="mb-8 text-center">
            <h2 className="mb-3 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-4xl font-extrabold tracking-tight text-brand-black">Open Positions</h2>
            <p className="text-xl font-medium text-brand-black">
              We're looking for talented individuals to join our growing team
            </p>
          </div>

          <div className="mx-auto max-w-4xl">
            <div className="grid gap-6 md:grid-cols-2">
              {positions.map((position, index) => (
                <Card key={index} className="rounded-3xl bg-brand-white shadow-xl transition-all hover:scale-105 hover:shadow-2xl">
                  <CardHeader>
                    <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-blue to-[#0066FF] shadow-lg">
                      <position.icon className="h-8 w-8 text-brand-white" />
                    </div>
                    <CardTitle className="text-xl font-bold text-brand-black">{position.title}</CardTitle>
                    <CardDescription className="text-base font-medium text-brand-black">{position.description}</CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section
        className="relative bg-brand-white py-12"
      //style={{
      //  transform: `translateY(${scrollY * 0.15}px)`,
      //}}
      >
        <div className="container">
          <div className="mx-auto max-w-2xl">
            <Card className="rounded-3xl bg-brand-white shadow-xl">
              <CardHeader>
                <CardTitle className="bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-2xl font-bold text-brand-black">Apply Now</CardTitle>
                <CardDescription className="text-base font-medium text-brand-black">
                  Fill out the form below and we'll get back to you within a week
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="font-bold text-brand-black">Full Name</Label>
                    <Input
                      id="name"
                      placeholder="Your full name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="rounded-xl font-medium shadow-md transition-all focus:shadow-lg"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="font-bold text-brand-black">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="rounded-xl font-medium shadow-md transition-all focus:shadow-lg"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="font-bold text-brand-black">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="rounded-xl font-medium shadow-md transition-all focus:shadow-lg"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="position" className="font-bold text-brand-black">Position</Label>
                    <Select
                      value={formData.position}
                      onValueChange={(value) => setFormData({ ...formData, position: value })}
                      required
                    >
                      <SelectTrigger id="position" className="rounded-xl font-medium shadow-md">
                        <SelectValue placeholder="Select a position" />
                      </SelectTrigger>
                      <SelectContent className="rounded-xl bg-brand-white shadow-xl">
                        <SelectItem value="software-engineer">Software Engineer</SelectItem>
                        <SelectItem value="ui-ux-designer">UI/UX Designer</SelectItem>
                        <SelectItem value="community-manager">Community Manager</SelectItem>
                        <SelectItem value="product-manager">Product Manager</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="experience" className="font-bold text-brand-black">Tell us about your experience</Label>
                    <Textarea
                      id="experience"
                      placeholder="Share your relevant experience, skills, and why you'd like to join UNILinkd..."
                      rows={6}
                      value={formData.experience}
                      onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                      required
                      className="rounded-xl font-medium shadow-md transition-all focus:shadow-lg"
                    />
                  </div>

                  <Button
                    type="button"
                    disabled={isSubmitting}  // ✅ ADDED
                    className="w-full rounded-xl bg-gradient-to-r from-brand-blue to-[#0066FF] text-lg font-bold text-brand-white shadow-xl transition-all hover:scale-105 hover:shadow-2xl disabled:opacity-50"
                    onClick={handleSubmit}
                  >
                    {isSubmitting ? (   // ✅ ADDED
                      <>
                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Send Message
                      </>
                    )}
                  </Button>
                  <CaptchaWidget />
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
